class WateEmailPopup extends HTMLElement {
  constructor() {
    super();

    this.isOpen = false;
    this.timer = null;
    this.abortController = new AbortController();

    this.storageKeys = {
      subscribed: 'wate:popup:subscribed',
      dismissedAt: 'wate:popup:dismissed-at',
    };
  }

  // Children are off-limits in the constructor, so cache them on connect --
  // this also re-runs after a theme-editor section reload.
  cacheElements() {
    this.overlay = this.querySelector('.wate-popup__overlay');
    this.dialog = this.querySelector('[role="dialog"]');
    this.form = this.querySelector('form');
    this.formState = this.querySelector('[data-state="form"]');
    this.successState = this.querySelector('[data-state="success"]');
    this.errorEl = this.querySelector('.wate-popup__error');
    // .wate-popup__submit is shared with the success CTA anchor, so scope it.
    this.submitButton = this.formState ? this.formState.querySelector('.wate-popup__submit') : null;

    return Boolean(this.overlay && this.dialog && this.formState && this.successState);
  }

  // ?popup=1 on any URL forces the popup open, ignoring every frequency rule.
  // This is the way to preview it once you have already dismissed or signed up.
  get isForced() {
    try {
      return new URLSearchParams(window.location.search).has('popup');
    } catch (error) {
      return false;
    }
  }

  connectedCallback() {
    if (!this.cacheElements()) {
      console.warn('[wate-email-popup] Incomplete markup, popup disabled.');
      return;
    }

    // getFocusableElements() still matches controls inside a [hidden] block, so
    // the inactive state has to be disabled or the tab trap latches onto an
    // invisible element and Tab escapes the dialog.
    this.setStateActive(this.formState, !this.formState.hidden);
    this.setStateActive(this.successState, !this.successState.hidden);

    this.bindDialogEvents();
    this.bindCopyButton();

    if (this.form) {
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    // The theme editor must never be ambushed by the popup: it opens only when
    // the merchant selects the section, and ignores every frequency rule.
    if (window.Shopify && window.Shopify.designMode) {
      this.bindDesignMode();
      return;
    }

    // A native (no-JS) form POST redirects back with Liquid having already
    // un-hidden the success state, so just reopen on top of it.
    if (!this.successState.hidden) {
      this.remember(this.storageKeys.subscribed, '1');
      this.open();
      return;
    }

    if (this.isForced) {
      this.open();
      return;
    }

    if (this.shouldSuppress()) return;

    this.armTriggers();
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    this.abortController.abort();

    // Only release the scroll lock if it was ours: the cart drawer shares the class.
    if (this.isOpen) document.body.classList.remove('overflow-hidden');
  }

  /* State --------------------------------------------------------------- */

  setStateActive(state, active) {
    state.hidden = !active;

    state.querySelectorAll('button, input, select, textarea').forEach((el) => {
      el.disabled = !active;
    });

    // Anchors have no disabled attribute; parking the href drops them out of
    // the a[href] focusable selector instead.
    state.querySelectorAll('a').forEach((el) => {
      if (active && el.dataset.href) {
        el.setAttribute('href', el.dataset.href);
        delete el.dataset.href;
      } else if (!active && el.hasAttribute('href')) {
        el.dataset.href = el.getAttribute('href');
        el.removeAttribute('href');
      }
    });
  }

  /* Storage ------------------------------------------------------------- */

  // localStorage throws in Safari private mode and when cookies are blocked.
  recall(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  remember(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (error) {
      /* Non-fatal: the popup simply reappears next visit. */
    }
  }

  shouldSuppress() {
    if (this.dataset.alreadySubscribed === 'true') return true;

    if (this.dataset.showOnMobile === 'false' && window.matchMedia('(max-width: 749px)').matches) {
      return true;
    }

    if (this.recall(this.storageKeys.subscribed) === '1') return true;

    const dismissedAt = parseInt(this.recall(this.storageKeys.dismissedAt), 10);
    if (!dismissedAt) return false;

    const days = parseInt(this.dataset.frequencyDays, 10) || 0;
    return Date.now() - dismissedAt < days * 24 * 60 * 60 * 1000;
  }

  /* Triggers ------------------------------------------------------------ */

  armTriggers() {
    const { signal } = this.abortController;
    const trigger = this.dataset.trigger || 'delay';
    const delay = (parseInt(this.dataset.delaySeconds, 10) || 0) * 1000;

    if (trigger === 'scroll') {
      const target = parseInt(this.dataset.scrollPercent, 10) || 30;
      const onScroll = throttle(() => {
        const scrollable = document.documentElement.scrollHeight - window.innerHeight;
        if (scrollable <= 0) return;
        if ((window.scrollY / scrollable) * 100 >= target) this.open();
      }, 200);

      window.addEventListener('scroll', onScroll, { passive: true, signal });
      return;
    }

    // Exit intent has no meaning on touch devices, so fall through to the delay.
    if (trigger === 'exit' && window.matchMedia('(pointer: fine)').matches) {
      document.addEventListener(
        'mouseout',
        (event) => {
          if (!event.relatedTarget && event.clientY <= 0) this.open();
        },
        { signal }
      );
      return;
    }

    this.timer = setTimeout(() => this.open(), delay);
  }

  bindDesignMode() {
    const { signal } = this.abortController;
    const sectionId = this.dataset.sectionId;
    const isOurs = (event) => event.detail.sectionId === sectionId;

    document.addEventListener('shopify:section:select', (event) => {
      if (isOurs(event)) this.open();
    }, { signal });

    document.addEventListener('shopify:section:deselect', (event) => {
      if (isOurs(event)) this.close({ remember: false });
    }, { signal });

    // A settings change re-renders the section, which fires section:load on the
    // fresh element -- reopen so the merchant sees the edit straight away. This
    // never fires on first page load, so opening the customiser stays quiet.
    document.addEventListener('shopify:section:load', (event) => {
      if (isOurs(event)) this.open();
    }, { signal });
  }

  /* Open / close -------------------------------------------------------- */

  bindDialogEvents() {
    const { signal } = this.abortController;

    this.querySelectorAll('[data-popup-close]').forEach((button) => {
      button.addEventListener('click', () => this.close(), { signal });
    });

    this.overlay.addEventListener(
      'click',
      (event) => {
        if (event.target === this.overlay) this.close();
      },
      { signal }
    );

    this.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape') this.close();
      },
      { signal }
    );
  }

  open() {
    if (this.isOpen) return;
    this.isOpen = true;
    clearTimeout(this.timer);

    document.body.classList.add('overflow-hidden');

    // With delay 0 the open attribute would land in the same frame as first
    // paint and the fade would be skipped, so give the closed state one frame.
    requestAnimationFrame(() => {
      this.setAttribute('open', '');

      const focusTarget = this.successState.hidden
        ? this.querySelector('input[type="email"]')
        : this.successState.querySelector('[tabindex="-1"]');

      trapFocus(this, focusTarget || this.dialog);
    });
  }

  close({ remember = true } = {}) {
    if (!this.isOpen) return;
    this.isOpen = false;

    this.removeAttribute('open');
    document.body.classList.remove('overflow-hidden');
    removeTrapFocus();

    if (remember) {
      this.remember(this.storageKeys.dismissedAt, String(Date.now()));
    }
  }

  /* Submission ---------------------------------------------------------- */

  async onSubmit(event) {
    event.preventDefault();

    this.setError('');
    this.submitButton.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(this.form.action, {
        method: 'POST',
        body: new FormData(this.form),
      });

      const html = await response.text();

      // Shopify only 302s to return_to + customer_posted=true when the customer
      // was actually accepted; a validation failure re-renders with 200 instead.
      // The redirect itself is the load-bearing signal -- the URL alone lies if
      // the page already carried customer_posted=true from an earlier submit.
      if (response.redirected && response.url.includes('customer_posted=true')) {
        this.showSuccess();
        return;
      }

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const node = doc.querySelector('[data-error-text]');
      const message = node ? node.textContent.trim() : '';

      if (message) {
        this.setError(message);
        return;
      }

      // Neither a confirmed success nor a usable error -- usually Shopify's bot
      // challenge. Hand it to a real form POST so its own flow can complete and
      // the address genuinely gets recorded, rather than faking success here.
      console.warn('[wate-email-popup] Submit unconfirmed, falling back to a native POST.', {
        status: response.status,
        redirected: response.redirected,
        url: response.url,
      });
      this.form.submit();
    } catch (error) {
      this.setError(this.dataset.genericError);
    } finally {
      this.submitButton.removeAttribute('aria-busy');
    }
  }

  showSuccess() {
    this.remember(this.storageKeys.subscribed, '1');

    this.setStateActive(this.formState, false);
    this.setStateActive(this.successState, true);

    // The trap's first/last elements just changed, so rebuild it.
    const heading = this.successState.querySelector('[tabindex="-1"]');
    trapFocus(this, heading || this.dialog);
  }

  setError(message) {
    if (!this.errorEl) return;

    this.errorEl.querySelector('[data-error-text]').textContent = message || '';
    this.errorEl.hidden = !message;
  }

  /* Copy ---------------------------------------------------------------- */

  bindCopyButton() {
    const button = this.querySelector('.wate-popup__copy');
    if (!button) return;

    button.addEventListener(
      'click',
      async () => {
        try {
          await navigator.clipboard.writeText(this.dataset.code);
        } catch (error) {
          // The Clipboard API needs a secure context. Select the code instead
          // so the visitor can copy it by hand.
          const range = document.createRange();
          range.selectNodeContents(this.querySelector('.wate-popup__code-value'));
          const selection = window.getSelection();
          selection.removeAllRanges();
          selection.addRange(range);
          return;
        }

        button.dataset.copied = 'true';
        setTimeout(() => delete button.dataset.copied, 2500);
      },
      { signal: this.abortController.signal }
    );
  }
}

customElements.define('wate-email-popup', WateEmailPopup);
